/**
 * CandidateCardGrid Component
 *
 * Uses the shared PersonCard for consistent card layout across the app.
 * Renders candidate-specific chips, stats, and social links.
 */

import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserCheck, Users, Briefcase, DollarSign, Landmark } from 'lucide-react';
import PersonCard, { PersonCardChip, PersonCardSocialLink } from '../PersonCard';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import { statusVariant } from '../../constants/candidates';
import { formatPersonName } from '../../utils/nameUtils';
import { BaseCandidate, OrgCandidate, CandidateRenderContext, SocialMediaLink } from './types';
import { toTrackedCandidateIdSet } from './candidateTrackedIds';
import { CandidateCustomFieldColumn, getCustomFieldDisplayValues } from './candidateCustomFieldUtils';
import { padDistrictNumber } from '../../utils/districtDisplay';

const SOCIAL_LABEL: Record<string, string> = {
  x: 'X (Twitter)', instagram: 'Instagram', facebook: 'Facebook',
  linkedin: 'LinkedIn', bluesky: 'Bluesky', youtube: 'YouTube',
};

const UNSPECIFIED_OFFICE = 'Unspecified Office';

interface CandidateCardGridProps {
  candidates: BaseCandidate[];
  context: CandidateRenderContext;
  mode?: 'directory' | 'workspace';
  gridCols?: string;
  className?: string;
  emptyMessage?: string;
  /** Report card view: section headers per office */
  groupByOffice?: boolean;
  /**
   * Organization custom fields to show on each card. Callers derive these from
   * the data, so a card only lists fields that actually hold a value.
   */
  customFieldColumns?: CandidateCustomFieldColumn[];
  /**
   * Hide the status chip. The report's All Candidates tab shows active
   * candidates only, so a chip reading "Active" on every card says nothing.
   */
  hideStatus?: boolean;
}

function getOfficeGroupKey(candidate: BaseCandidate): string {
  return candidate.officeSought?.trim() || UNSPECIFIED_OFFICE;
}

function groupCandidatesByOffice(candidates: BaseCandidate[]): Array<[string, BaseCandidate[]]> {
  const map = new Map<string, BaseCandidate[]>();
  for (const candidate of candidates) {
    const key = getOfficeGroupKey(candidate);
    const group = map.get(key);
    if (group) {
      group.push(candidate);
    } else {
      map.set(key, [candidate]);
    }
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

const CandidateCardGrid: React.FC<CandidateCardGridProps> = ({
  candidates,
  context,
  mode = 'directory',
  gridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  className = '',
  emptyMessage = 'No candidates found matching your filters.',
  groupByOffice = false,
  customFieldColumns = [],
  hideStatus = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = location.pathname + location.search;
  const candidateBase = context.isPublicView ? '/public/candidates' : '/candidates';
  const legislatorBase = context.isPublicView ? '/public/legislators' : '/legislators';
  const trackedIdSet = toTrackedCandidateIdSet(context.trackedCandidateIds);

  const officeGroups = useMemo(
    () => (groupByOffice ? groupCandidatesByOffice(candidates) : null),
    [candidates, groupByOffice]
  );

  if (candidates.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-text-secondary dark:text-text-dark-secondary">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const renderCandidateCard = (candidate: BaseCandidate) => {
    const chips: PersonCardChip[] = hideStatus ? [] : [
      {
        label: candidate.status?.charAt(0).toUpperCase() + candidate.status?.slice(1),
        variant: statusVariant(candidate.status),
      },
    ];

    const inlineText = candidate.officeSought
      + (candidate.district ? ` · District ${padDistrictNumber(candidate.district)}` : '');

    const socialLinks: PersonCardSocialLink[] = (candidate.socialMedia || []).map((sm: SocialMediaLink) => ({
      platform: sm.platform,
      label: SOCIAL_LABEL[sm.platform] || sm.platform,
      url: sm.url,
      title: `@${sm.handle}`,
    }));

    const hasFunds = candidate.totalFundsRaised != null && candidate.totalFundsRaised > 0;
    const hasStats = hasFunds
      || (candidate.activeStaffCount != null && candidate.activeStaffCount > 0)
      || candidate.campaignManagerName
      || candidate.seatIncumbentName;

    const orgCandidate = candidate as OrgCandidate;
    const workspaces = orgCandidate.workspaces;

    return (
      <PersonCard
        key={candidate.id}
        id={candidate.id}
        name={formatPersonName(candidate)}
        party={candidate.partyAffiliation}
        nameHref={`${candidateBase}/${candidate.id}`}
        nameLinkState={{ returnUrl: currentUrl }}
        onNameClick={() => navigate(`${candidateBase}/${candidate.id}`, { state: { returnUrl: currentUrl } })}
        photoUrl={candidate.photoUrl}
        fallbackIcon={UserCheck}
        chips={chips}
        inlineText={inlineText}
        isPriority={candidate.isPriority}
        isSelected={context.selectedCandidateIds?.has(candidate.id)}
        isTracked={trackedIdSet.has(candidate.id)}
        onSelect={context.onSelectCandidate
          ? (checked) => context.onSelectCandidate!(candidate.id, checked)
          : undefined}
        onBookmark={context.onBookmark
          ? () => context.onBookmark!(candidate.id, candidate.fullName)
          : undefined}
        bookmarkTitle={trackedIdSet.has(candidate.id) ? 'Manage tracking' : 'Track candidate'}
        email={candidate.email}
        phone={candidate.phone}
        website={candidate.website}
        socialLinks={socialLinks.length > 0 ? socialLinks : undefined}
        onClick={() => {
          if (context.onNavigate) context.onNavigate(candidate.id);
          else navigate(`${candidateBase}/${candidate.id}`);
        }}
        badges={workspaces && workspaces.length > 0 ? (
          <WorkspaceBadgeList workspaces={workspaces} className="mb-3" />
        ) : undefined}
        statsRow={hasStats ? (
          /* PersonCard lays the slot out as a flex row; these are stacked
             lines, so they need their own column or a narrow card squeezes
             funds, incumbent and custom fields side by side. */
          <div className="w-full space-y-1.5">
            {/* One data element per row on a phone (Angie, 2026-09-13); a row again from sm up. */}
            <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              {hasFunds && (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4 text-text-tertiary" />
                  <span>{candidate.totalFundsRaised!.toLocaleString()} Raised</span>
                </span>
              )}
              {candidate.campaignManagerName && (
                <span className="flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-text-tertiary" />
                  <span className="truncate">Mgr: {candidate.campaignManagerName}</span>
                </span>
              )}
              {candidate.activeStaffCount != null && candidate.activeStaffCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-text-tertiary" />
                  <span>{candidate.activeStaffCount} Staff</span>
                </span>
              )}
            </div>
            {candidate.seatIncumbentName && (
              <div className="flex items-center gap-1.5 text-sm text-text-secondary dark:text-text-dark-secondary">
                <Landmark className="w-4 h-4 text-text-tertiary" />
                <span>
                  Inc:{' '}
                  {candidate.seatIncumbentId ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${legislatorBase}/${candidate.seatIncumbentId}`);
                      }}
                      className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {candidate.seatIncumbentName}
                    </button>
                  ) : (
                    <span className="font-medium">{candidate.seatIncumbentName}</span>
                  )}
                </span>
              </div>
            )}
            {customFieldColumns.map((col) => {
              const displayValues = getCustomFieldDisplayValues(candidate, col.definitionId);
              if (displayValues.length === 0) return null;
              return (
                <div
                  key={col.definitionId}
                  className="text-sm text-text-secondary dark:text-text-dark-secondary break-words"
                >
                  <span className="text-text-tertiary dark:text-text-dark-tertiary">{col.name}:</span>{' '}
                  {displayValues.map((display, index) => (
                    <span key={index} className="font-medium text-text-primary dark:text-text-dark-primary">
                      {index > 0 && '; '}
                      {display.text}
                      {display.workspaceName && (
                        <span className="font-normal text-text-tertiary dark:text-text-dark-tertiary">
                          {' '}({display.workspaceName})
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        ) : undefined}
        footerRow={mode === 'workspace' ? (
          <div className="pt-2 mt-2 border-t border-border-light dark:border-border-dark text-xs text-text-tertiary dark:text-text-dark-tertiary flex items-center justify-between">
            {candidate.position ? (
              <span>Position: <span className="font-medium">{candidate.position}</span></span>
            ) : <span />}
            {context.workspaceId && (
              <CustomFieldBadge
                entityType="candidate"
                entityId={candidate.id}
                entityDisplayName={candidate.fullName}
                workspaceId={context.workspaceId}
                count={candidate.customFieldCount}
                canEdit={context.permissions?.canEdit}
              />
            )}
          </div>
        ) : undefined}
      />
    );
  };

  if (officeGroups) {
    return (
      <div className={`space-y-8 ${className}`}>
        {officeGroups.map(([office, groupCandidates]) => (
          <section key={office}>
            <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-4 pb-2 border-b border-border-light dark:border-border-dark">
              {office}
              <span className="ml-2 text-sm font-normal text-text-secondary dark:text-text-dark-secondary">
                ({groupCandidates.length})
              </span>
            </h3>
            <div className={`grid ${gridCols} gap-4`}>
              {groupCandidates.map(renderCandidateCard)}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className={`grid ${gridCols} gap-4 ${className}`}>
      {candidates.map(renderCandidateCard)}
    </div>
  );
};

export default CandidateCardGrid;
